import { DEFAULT_MAX_AGE_MS, type Definition } from './registry';

/** Bump when the shape changes. The chrome layer rejects any other value and falls back to registry defaults. */
export const GATE_ENVELOPE_SCHEMA = 2 as const;

/** The `chrome.storage.local` key the envelope lives under. Mirrored in `extension-chrome` `StoreKeys`. */
export const GATE_ENVELOPE_STORE_KEY = 'feature_gate_envelope';

/** The window message type the frontend uses to hand the envelope to the extension page hosting it. */
export const SET_FEATURE_GATE_ENVELOPE_MESSAGE_TYPE = 'SET_FEATURE_GATE_ENVELOPE';

/**
 * Structural mirror of the feature-gate client's `Identifiers`. Kept local so this package has no
 * dependency on the SDK and the contract cannot drift when the SDK's type changes.
 */
export type GateIdentifiers = Readonly<Record<string, string | undefined>>;

/** Structural mirror of the feature-gate client's `CustomAttributes`. */
export type GateCustomAttributes = Readonly<
	Record<string, string | number | boolean | ReadonlyArray<string>>
>;

/**
 * Structural mirror of the polling provider's `FrontendExperimentsResult`: the raw values blob plus
 * the client SDK key the provider fetched alongside it.
 */
export interface ProviderValues {
	readonly experimentValues: Readonly<Record<string, unknown>>;
	readonly customAttributesFromFetch?: GateCustomAttributes;
	readonly clientSdkKey?: string;
}

/**
 * The `initializeValues` argument, reduced to the registered keys and nothing else: not the
 * provider's blob, which also carries every other evaluated object and the identity it keyed on.
 * See `host/sanitise-values.ts` for what is dropped and why.
 */
export interface GateEnvelopePayload {
	readonly experimentValues: Readonly<Record<string, unknown>>;
}

export interface GateEnvelope {
	readonly schema: typeof GATE_ENVELOPE_SCHEMA;
	/**
	 * Strictly increasing per publish from one frontend instance; the receiver refuses a lower one for
	 * the same profile. The chrome layer pins what it hydrated from and never re-reads.
	 */
	readonly generation: number;
	/** `Date.now()` when the frontend read the values. Drives the per-entry stale policy. */
	readonly fetchedAt: number;
	/**
	 * {@link computeProfileHash} of `{ targetApp, identifiers, customAttributes }`. One-way on purpose:
	 * enough to tell one profile's envelope from another's, with no account or tenant id in storage.
	 */
	readonly profileHash: string;
	readonly targetApp: string;
	readonly payload: GateEnvelopePayload;
}

export interface SetFeatureGateEnvelopeMessage {
	readonly type: typeof SET_FEATURE_GATE_ENVELOPE_MESSAGE_TYPE;
	readonly value: GateEnvelope;
}

export type GateEnvelopeRejection =
	| 'not-an-object'
	| 'wrong-schema'
	| 'invalid-generation'
	| 'invalid-fetched-at'
	| 'missing-profile-hash'
	| 'missing-target-app'
	| 'missing-payload'
	| 'identity-present'
	| 'client-sdk-key-present';

export type GateEnvelopeValidation =
	| { readonly ok: true; readonly envelope: GateEnvelope }
	| { readonly ok: false; readonly reason: GateEnvelopeRejection };

/**
 * Structural validation only, and synchronous: it runs on the receiving side before anything is
 * written to storage, and again on the reading side before hydration. Nothing here needs WebCrypto,
 * which matters because a content script on a plain `http://` page does not have it.
 */
export function validateGateEnvelope(value: unknown): GateEnvelopeValidation {
	if (!isRecord(value)) {
		return { ok: false, reason: 'not-an-object' };
	}

	if (value.schema !== GATE_ENVELOPE_SCHEMA) {
		return { ok: false, reason: 'wrong-schema' };
	}

	if (!isFiniteNonNegative(value.generation)) {
		return { ok: false, reason: 'invalid-generation' };
	}

	if (!isFiniteNonNegative(value.fetchedAt)) {
		return { ok: false, reason: 'invalid-fetched-at' };
	}

	if (typeof value.profileHash !== 'string' || value.profileHash.length === 0) {
		return { ok: false, reason: 'missing-profile-hash' };
	}

	if (typeof value.targetApp !== 'string' || value.targetApp.length === 0) {
		return { ok: false, reason: 'missing-target-app' };
	}

	if (!isRecord(value.payload) || !isRecord(value.payload.experimentValues)) {
		return { ok: false, reason: 'missing-payload' };
	}

	// The "no FFS key in the CRX" property depends on this. Refuse, do not strip: a producer that
	// forgot to strip is a bug worth surfacing.
	if ('clientSdkKey' in value.payload || 'clientSdkKey' in value) {
		return { ok: false, reason: 'client-sdk-key-present' };
	}

	// Same treatment for identity: nothing here reads one, so a producer that includes one is
	// shipping it to disk for nothing.
	if (carriesIdentity(value, value.payload)) {
		return { ok: false, reason: 'identity-present' };
	}

	return { ok: true, envelope: value as unknown as GateEnvelope };
}

/**
 * Fields that would put identity in storage, at the three levels a producer could leave one: the
 * envelope, the payload, and the values blob. The chrome layer reads none of them, and `profileHash`
 * already covers the only thing the receiver needs.
 */
const ENVELOPE_IDENTITY_FIELDS = ['identifiers', 'customAttributes'] as const;
const PAYLOAD_IDENTITY_FIELDS = ['customAttributesFromFetch'] as const;
const VALUES_IDENTITY_FIELDS = ['user', 'user_hash', 'evaluated_keys', 'derived_fields'] as const;

function carriesIdentity(
	envelope: Record<string, unknown>,
	payload: Record<string, unknown>,
): boolean {
	if (ENVELOPE_IDENTITY_FIELDS.some((field) => field in envelope)) {
		return true;
	}
	if (PAYLOAD_IDENTITY_FIELDS.some((field) => field in payload)) {
		return true;
	}
	const values = payload.experimentValues;
	return isRecord(values) && VALUES_IDENTITY_FIELDS.some((field) => field in values);
}

/** Whether `envelope` is older than the entry's `maxAgeMs`. The caller applies `onStale`. */
export function isGateEnvelopeStale(
	envelope: Pick<GateEnvelope, 'fetchedAt'>,
	definition: Pick<Definition, 'maxAgeMs'>,
	now: number = Date.now(),
): boolean {
	const maxAgeMs = definition.maxAgeMs ?? DEFAULT_MAX_AGE_MS;
	return now - envelope.fetchedAt > maxAgeMs;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNonNegative(value: unknown): value is number {
	return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}
